import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { normalizeWorkTask, type WorkChecklistItem, type WorkStatus } from '@klothiq/domain';
import type { WorkTask } from '@klothiq/domain';
import type { PlanDetailsResponse } from '@/hooks/usePlans';
import {
  workRpc,
  workErrorMessage,
  WORK_QUERY_KEYS,
  type WorkMutationResult,
} from '@/lib/work-planning';

interface Intent {
  request_key: string;
}
interface PlanFields {
  title: string;
  description?: string | null;
  goal?: string | null;
  start_date?: string | null;
  target_date?: string | null;
  priority?: string;
}
interface TaskFields {
  title?: string;
  description?: string | null;
  priority?: string;
  due_date?: string | null;
  checklist?: WorkChecklistItem[];
  deadline_exception_reason?: string | null;
}
interface TaskEdit extends Intent {
  task_id: string;
  expected_version: number;
  plan_id?: string;
}

function useWorkMutation<T extends Intent>(rpc: string, message: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: T) => workRpc<WorkMutationResult>(rpc, { p_data: input }),
    onSuccess: async (result) => {
      if (result.task) {
        const task = normalizeWorkTask(result.task);
        client.setQueriesData<PlanDetailsResponse>(
          { queryKey: ['plan_details', task.plan_id] },
          (old) =>
            old
              ? {
                  ...old,
                  tasks: old.tasks.some((row) => row.id === task.id)
                    ? old.tasks.map((row) => (row.id === task.id ? task : row))
                    : [...old.tasks, task],
                }
              : old,
        );
        for (const [key] of client.getQueriesData<WorkTask[]>({ queryKey: ['my_tasks'] })) {
          client.setQueryData<WorkTask[]>(key, (old) =>
            Array.isArray(old)
              ? old
                  .filter(
                    (row) =>
                      row.id !== task.id || (task.assignee_id === key[1] && !task.plan_is_archived),
                  )
                  .map((row) => (row.id === task.id ? { ...row, ...task } : row))
              : old,
          );
        }
      }
      if (result.plan) {
        client.setQueriesData<PlanDetailsResponse>(
          { queryKey: ['plan_details', result.plan.id] },
          (old) => (old ? { ...old, plan: { ...old.plan, ...result.plan } } : old),
        );
      }
      await Promise.all(
        WORK_QUERY_KEYS.map((key) => client.invalidateQueries({ queryKey: [key] })),
      );
      toast.success(result.changed === false ? 'These details are already up to date.' : message);
    },
    onError: (error) => toast.error(workErrorMessage(error)),
  });
}

export function useCreatePlan() {
  return useWorkMutation<Intent & PlanFields>('create_work_plan_v2', 'Plan created');
}
export function useUpdatePlan() {
  return useWorkMutation<
    Intent & Partial<PlanFields> & { plan_id: string; expected_version: number }
  >('update_work_plan_v2', 'Plan updated');
}
export function useArchivePlan() {
  return useWorkMutation<Intent & { plan_id: string; expected_version: number; archived: boolean }>(
    'set_work_plan_archive_v2',
    'Plan archive status updated',
  );
}
export function useCreateTask() {
  return useWorkMutation<
    Intent & TaskFields & { plan_id: string; title: string; assignee_id?: string | null }
  >('create_work_task_v2', 'Task created');
}
export function useUpdateTask() {
  return useWorkMutation<TaskEdit & TaskFields>('update_work_task_v2', 'Task updated');
}
export function useReassignTask() {
  return useWorkMutation<TaskEdit & { new_assignee_id: string | null; reason?: string | null }>(
    'reassign_work_task_v2',
    'Task reassigned',
  );
}
export function useUpdateTaskProgressStatus() {
  return useWorkMutation<
    TaskEdit & {
      status: WorkStatus;
      progress: number;
      blocker_note?: string | null;
      reopen_note?: string | null;
    }
  >('set_work_task_status_v2', 'Progress updated');
}
export function useAddTaskComment() {
  return useWorkMutation<TaskEdit & { comment_text: string }>(
    'add_work_task_comment_v2',
    'Comment added',
  );
}
